import CommonField from 'common/components/entities/Field/FieldUi'
import FieldWrapperUi from 'common/components/entities/Field/FieldWrapperUi'
import FieldIconUiNew from 'common/components/entities/Field/field-icon-ui'
import { BaseFieldInterface } from 'common/types/entities/field-interface'
import debounce from 'lodash/debounce'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import usePage from 'publisher/hooks/usePage'
import { getGlobalFontFamily } from 'publisher/reducers/pageReducer'
import fieldValidation from 'publisher/utils/fieldValidation'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCalculateFormFieldStyles } from 'src/publisher/shared/hooks/use-calculate-form-fields-styles'

export interface BasePublisherBaseFieldProps {
  entity: BaseFieldInterface
  value: string | undefined
  onChange: (value: string | undefined) => void
  errors: string[]
  needInitialise?: boolean
  type?: string
  readOnly?: boolean
  disabled?: boolean
}

export const BasePublisherBaseField = ({
  entity,
  value,
  errors,
  onChange,
  needInitialise = true,
  type = 'text',
  readOnly,
  disabled,
}: BasePublisherBaseFieldProps) => {
  const [isClient, setIsClient] = useState(false)
  const [valueState, setValueState] = useState(value)
  const [inlineErrors, setInlineErrors] = useState([])
  const globalFontFamily = usePage(getGlobalFontFamily)
  const { t } = useTranslation()

  const updateReduxValue = useCallback(
    debounce((value?: string) => {
      onChange(value)
    }, 100),
    [],
  )

  const handleChange = useCallback(
    (value?: string) => {
      if (entity.slug) {
        setValueState(value)
        updateReduxValue(value)
      }
    },
    [updateReduxValue, entity.slug],
  )

  const handleBlur = useCallback(
    (value?: string) => {
      const errors = fieldValidation(entity.slug, value, entity.optional)
      setInlineErrors(errors.map((error: string) => t(error)))
      setTimeout(() => {
        window.dispatchEvent(new Event('form_updated'))
      }, 100)
    },
    [t, entity.slug, entity.optional],
  )

  const calculatedErrors = inlineErrors.length > 0 ? inlineErrors : errors

  // need to init all fields
  // biome-ignore lint/correctness/useExhaustiveDependencies: supposed to be fired once
  useEffect(() => {
    if (needInitialise) {
      // NOTE: we don't need debounce here
      onChange(value)
    }
  }, [needInitialise])

  useEffect(() => {
    if (value !== valueState) {
      setValueState(value)
    }
  }, [value])

  useEffect(() => {
    setIsClient(true)
  }, [])

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    handleChange(e.target.value)

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) =>
    handleBlur(e.target.value)

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedTextAlign,
    // calculatedLetterSpacing,
    // calculatedFontFamily,
  } = useCalculateFormFieldStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    fontSize: entity.fontSize,
    mobileFontSize: entity.mobileFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    // fontFamily: fontFamily,
    // mobileFontFamily: fontFamily,
    textAlign: entity.textAlign,
    mobileTextAlign: entity.mobileTextAlign,
  })

  return (
    isClient && (
      <StyleWrapper
        margin={entity.margin}
        mobileMargin={entity.mobileMargin}
        appearance={entity.appearance}
      >
        <FieldWrapperUi>
          {entity.iconClassName && (
            <FieldIconUiNew
              color={entity.color || 'initial'}
              fontSize={calculatedFontSize.desktop}
              mobileFontSize={calculatedFontSize.mobile}
              mobileColor={entity.mobileColor || entity.color || 'initial'}
              className={entity.iconClassName}
            />
          )}
          <CommonField
            id={entity.htmlAttrId}
            fontFamily={
              globalFontFamily === 'inherit' ? 'initial' : globalFontFamily
            }
            name={entity.slug}
            type={type}
            placeholder={entity.placeholder}
            padding={calculatedPadding.desktop}
            mobilePadding={calculatedPadding.mobile}
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            textAlign={calculatedTextAlign.desktop}
            mobileTextAlign={calculatedTextAlign.mobile}
            border={calculatedBorder.desktop}
            mobileBorder={calculatedBorder.mobile}
            backgroundColor={entity.backgroundColor}
            mobileBackgroundColor={entity.mobileBackgroundColor}
            placeholderColor={entity.placeholderColor}
            mobilePlaceholderColor={entity.mobilePlaceholderColor}
            onChange={onInputChange}
            onBlur={onInputBlur}
            color={entity.color}
            mobileColor={entity.mobileColor}
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            boxShadow={entity.boxShadow}
            mobileBoxShadow={entity.mobileBoxShadow}
            hasErrors={calculatedErrors.length > 0}
            disabled={disabled}
            value={valueState || ''}
            readOnly={readOnly}
          />
        </FieldWrapperUi>
        <FieldErrors errors={calculatedErrors} />
      </StyleWrapper>
    )
  )
}
